'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import { FederatedPhotoWithFallback as FederatedPhoto } from '@/src/components/FederatedPhoto/FederatedPhotoWithFallback';
import { FederatedPhotoPreload } from '@/src/components/FederatedPhoto';
import type { FederatedPhotoTreatment } from '@/src/components/FederatedPhoto/federatedPhoto';
import { buildListingPhotoAlt } from '@/src/lib/utils/listingUtils';
import type { HaloPhoto as Photo } from '@/src/components/features/property/lib/getHaloPhotos';
import { PropertyPhotoFallback } from '@/src/components/features/property/PropertyPhotoFallback';
import { MIN_LG } from '@/src/lib/breakpoints';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { DesktopGalleryButtons } from './DesktopGalleryButtons';
import { ImageCounter } from './ImageCounter';
import { MobileGalleryButtons } from './MobileGalleryButtons';
import * as styles from './photoGallery.styles';

type PhotoGalleryProps = {
  photos: Array<Photo>;
  displayName?: string;
  onAllPhotosClick: () => void;
  onGalleryTap?: (index: number) => void;
  onPhotoClick?: (index: number) => void;
  initialIndex?: number;
  has3dTour?: boolean;
  hasVideos?: boolean;
  hasStreetView?: boolean;
  on3dTourClick?: () => void;
  onVideosClick?: () => void;
  onStreetViewClick?: () => void;
};

const getBentoGridModifier = (photoCount: number): string => {
  switch (photoCount) {
    case 1:
      return styles.desktopBentoGrid1;
    case 2:
      return styles.desktopBentoGrid2;
    case 3:
      return styles.desktopBentoGrid3;
    default:
      return styles.desktopBentoGrid4;
  }
};

const HERO_PHOTO_SIZES = `${MIN_LG} 50vw, 100vw`;
const BENTO_WIDE_SIZES = `(min-width: 1600px) 480px, ${MIN_LG} 30vw, 100vw`;
const BENTO_NARROW_SIZES = `(min-width: 1600px) 320px, ${MIN_LG} 20vw, 100vw`;

// Half-height bento cells are width-bound; full-height cells stay height-bound by object-fit: cover.
const getBentoPhotoSizes = (index: number, photoCount: number): string => {
  if (photoCount >= 4) return index === 0 || index === 3 ? BENTO_WIDE_SIZES : BENTO_NARROW_SIZES;
  if (photoCount === 3 && index > 0) return BENTO_NARROW_SIZES;
  return HERO_PHOTO_SIZES;
};
/**
 * High-DPR phones (2.3-3x) at 100vw land in the 1152w srcset slot, but a DPR-2
 * cap only needs ~824 device px there — serve the cc_ft_960 render instead
 * (~28% fewer bytes on the LCP-critical hero). Slots real desktops select
 * (768w at dpr1, 1536w at dpr2) keep their native renders.
 */
const HERO_PHOTO_TREATMENT: FederatedPhotoTreatment = {
  defaultTreatment: 'cc_ft_768',
  srcSetTreatments: [
    { width: 192, treatment: 'cc_ft_192' },
    { width: 384, treatment: 'cc_ft_384' },
    { width: 576, treatment: 'cc_ft_576' },
    { width: 768, treatment: 'cc_ft_768' },
    { width: 960, treatment: 'cc_ft_960' },
    { width: 1152, treatment: 'cc_ft_960' },
    { width: 1536, treatment: 'cc_ft_1536' },
  ],
  placeholderTreatment: 'cc_ft_192',
};

export const PhotoGallery = ({
  photos,
  displayName,
  onAllPhotosClick,
  onGalleryTap,
  onPhotoClick,
  initialIndex = 0,
  has3dTour,
  hasVideos,
  hasStreetView,
  on3dTourClick,
  onVideosClick,
  onStreetViewClick,
}: PhotoGalleryProps) => {
  const t = useTranslations('hdp');
  const isDesktop = useIsDesktop();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [hasHydrated, setHasHydrated] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const didScrollRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  // Scroll to initialIndex on mount
  useEffect(() => {
    if (!hasHydrated || isDesktop) return;

    const track = trackRef.current;
    if (!track || initialIndex === 0) return;

    requestAnimationFrame(() => {
      isScrollingRef.current = true;
      track.scrollTo({ left: initialIndex * track.clientWidth, behavior: 'instant' });
      requestAnimationFrame(() => {
        isScrollingRef.current = false;
      });
    });
  }, [hasHydrated, initialIndex, isDesktop]);

  // Sync currentIndex from scroll position
  useEffect(() => {
    if (isDesktop) return;

    const track = trackRef.current;
    if (!track) return;

    let rafId: number;
    const handleScroll = () => {
      didScrollRef.current = true;
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        didScrollRef.current = false;
      }, 300);

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isScrollingRef.current) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setCurrentIndex(index);
      });
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      clearTimeout(scrollTimerRef.current);
    };
  }, [isDesktop]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const clamped = Math.max(0, Math.min(index, photos.length - 1));
      isScrollingRef.current = true;
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'instant' });
      setCurrentIndex(clamped);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      });
    },
    [photos.length],
  );

  const goToNext = useCallback(() => {
    scrollToIndex(currentIndex + 1);
  }, [currentIndex, scrollToIndex]);

  const goToPrev = useCallback(() => {
    scrollToIndex(currentIndex - 1);
  }, [currentIndex, scrollToIndex]);

  const handleCarouselClick = useCallback(() => {
    if (!didScrollRef.current) {
      onGalleryTap?.(currentIndex);
    }
  }, [currentIndex, onGalleryTap]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    },
    [goToNext, goToPrev],
  );

  const handlePhotoClick = useCallback(
    (index: number) => {
      if (didScrollRef.current) return;

      if (isDesktop) {
        if (onPhotoClick) {
          onPhotoClick(index);
          return;
        }
        onAllPhotosClick();
        return;
      }

      onGalleryTap?.(index);
    },
    [isDesktop, onAllPhotosClick, onGalleryTap, onPhotoClick],
  );

  if (photos.length === 0) return null;

  const realPhotoCount = photos.filter((photo) => !photo.noPhoto).length;
  const hasRealPhotos = realPhotoCount > 0;
  const hasMultiplePhotos = realPhotoCount > 1;
  const sharedPhotos = photos.slice(0, 4);
  const bentoGridModifier = getBentoGridModifier(sharedPhotos.length);
  const mobileOnlyPhotos = hasHydrated && !isDesktop ? photos.slice(4) : [];
  const visiblePhotos = [...sharedPhotos, ...mobileOnlyPhotos];
  const getPhotoLoading = (index: number) => {
    if (index === 0) return undefined;
    // Gate the neighbor prefetch on hydration: an eager off-screen neighbor in
    // SSR HTML can't be cancelled and steals bandwidth from the LCP hero. After
    // hydration the re-render flips the neighbor to eager so swipes stay instant.
    return hasHydrated && !isDesktop && Math.abs(index - currentIndex) <= 1 ? 'eager' : 'lazy';
  };

  const galleryContent = (
    <div
      ref={containerRef}
      className={cx(styles.galleryViewport, styles.desktopBentoGridBase)}
      role="region"
      aria-roledescription={isDesktop || !hasRealPhotos ? undefined : 'carousel'}
      aria-label={t('propertyPhotos')}
      tabIndex={isDesktop || !hasRealPhotos ? undefined : 0}
      onKeyDown={isDesktop || !hasRealPhotos ? undefined : handleKeyDown}
      onClick={isDesktop || !hasRealPhotos ? undefined : handleCarouselClick}
      data-testid="hero-photos-grid"
      data-hydrated={hasHydrated}
    >
      <div ref={trackRef} className={cx(styles.galleryTrack, bentoGridModifier)}>
        {visiblePhotos.map((photo, i) => {
          if (photo.noPhoto) {
            return (
              <div key={i} className={cx(styles.gallerySlide, styles.bentoStatic)}>
                <PropertyPhotoFallback altText={photo.caption} className={cx(styles.galleryImage, styles.bentoImage)} />
              </div>
            );
          }

          return (
            <button
              key={i}
              type="button"
              className={cx(styles.gallerySlide, styles.bentoButton)}
              onClick={(event) => {
                event.stopPropagation();
                handlePhotoClick(i);
              }}
              aria-label={t('viewPhoto', { n: i + 1 })}
            >
              <FederatedPhoto
                src={photo.url}
                defaultSize={768}
                sizes={i < sharedPhotos.length ? getBentoPhotoSizes(i, sharedPhotos.length) : HERO_PHOTO_SIZES}
                treatment={HERO_PHOTO_TREATMENT}
                alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                className={cx(styles.galleryImage, styles.bentoImage)}
                loading={getPhotoLoading(i)}
                priority={i === 0}
                draggable={false}
              />
            </button>
          );
        })}
      </div>

      {hasRealPhotos ? (
        <ImageCounter
          currentIndex={currentIndex}
          totalCount={photos.length}
          onClick={onGalleryTap ? () => onGalleryTap(currentIndex) : onAllPhotosClick}
        />
      ) : null}
      <MobileGalleryButtons
        hasVideos={hasVideos}
        has3dTour={has3dTour}
        hasStreetView={hasStreetView}
        onVideosClick={onVideosClick}
        on3dTourClick={on3dTourClick}
        onStreetViewClick={onStreetViewClick}
      />
      <DesktopGalleryButtons
        onAllPhotosClick={onAllPhotosClick}
        hasMultiplePhotos={hasMultiplePhotos}
        has3dTour={has3dTour}
        hasVideos={hasVideos}
        hasStreetView={hasStreetView}
        on3dTourClick={on3dTourClick}
        onVideosClick={onVideosClick}
        onStreetViewClick={onStreetViewClick}
      />
    </div>
  );

  return (
    <div className={styles.galleryContainer} data-admin-tools-anchor="photo-gallery">
      {/* The lg+ bento renders photos 1-4 above the fold, but SSR can't know
          the viewport: fetchpriority/eager on the <img>s would re-inflate
          mobile (HP-8668), so the parser-time high-priority hint is scoped to
          lg+ via media preloads and the <img> markup stays untouched. The hero
          (index 0) already preloads on both breakpoints via `priority`. */}
      {sharedPhotos
        .slice(1)
        .map((photo, i) =>
          photo.noPhoto ? null : (
            <FederatedPhotoPreload
              key={`bento-preload-${i + 1}`}
              src={photo.url}
              defaultSize={768}
              sizes={getBentoPhotoSizes(i + 1, sharedPhotos.length)}
              treatment={HERO_PHOTO_TREATMENT}
              media={MIN_LG}
            />
          ),
        )}
      {galleryContent}
    </div>
  );
};
